import { useActiveCircle } from '@/hooks/use-active-circle';
import { useIsConciergeOnboardingEnabled } from '@/hooks/use-is-concierge-onboarding-enabled';
import { useLeaveWorkflow } from '@/hooks/use-update-workflow';
import {
    AmplitudeFeatureFlagNames,
    AmplitudeExperimentNames,
} from '@/libs/feature-flags';
import { routes } from '@/utils/routes';
import { getLocalStorageUserSha } from '@/utils/storage';
import {
    WorkflowStepDefinition,
    WorkflowWorkflowState,
} from '@calm/evexia-web-sdk';
import { useAmplitudeExperiment } from '@rhg/hooks/use-experiments';
import { useAllWorkflowInstances } from '@rhg/hooks/use-workflows';
import useAuth from '@rhg/libs/auth0/use-auth';
import { logger } from '@rhg/libs/logger';
import {
    GOALS_TOPICS_INFO_VIDEO_ID,
    CONDITIONS_INFO_VIDEO_ID,
    SCREENING_INFO_VIDEO_ID,
    INTRO_TO_CARE_PLAN_VIDEO_ID,
} from '@rhg/utils/constants';
import {
    WorkflowIds,
    FtueStepDefinitionIds,
    FtueStoredStepName,
    STORED_RETAKING_SURVEY_KEY,
} from '@rhg/utils/workflows';
import { useRouter } from 'next/router';
import {
    createContext,
    ReactNode,
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import { useGetPreviousFtueWorkflowStep } from './hooks/use-get-previous-ftue-workflow-step';
import { useRestartFtueWorkflow } from './hooks/use-restart-ftue-workflow';
import { useSkipCachedVideoStepBackward } from './hooks/use-skip-cached-video-step-backward';
import { useSkipCachedVideoStepForward } from './hooks/use-skip-cached-video-step-forward';
import { useStartFtueWorkflow } from './hooks/use-start-ftue-workflow';
import { useUpdateFtueWorkflowStep } from './hooks/use-update-ftue-workflow-step';
import { LastKnownWorkflowStep, WorkflowState } from './types';
import {
    getStoredStep,
    removeStoredStep,
    storeWorkflowStep,
    getStoredWorkflowStep,
    removeStoredWorkflowStep,
    storeIsRetakingSurvey,
    getIsRetakingSurvey,
    storeCurrentStep,
} from './utils';

export const WorkflowContext = createContext<WorkflowState>(undefined!);

export function FtueWorkflowProvider({ children }: { children: ReactNode }) {
    const hasRestartedWorkflow = useRef(false);
    const hasAbandonedConflictingWorkflow = useRef(false);
    /* Mirror the refs above in state so `isResolvingStep` re-renders once each
     * attempt settles - a ref read can't invalidate a render. */
    const [hasSettledRestart, setHasSettledRestart] = useState(false);
    const [hasSettledAbandon, setHasSettledAbandon] = useState(false);
    const { user, isLoading, isAuthenticated } = useAuth();
    const {
        isEnrolled: isFtueWorkflowEnabled,
        isLoading: isFtueWorkflowLoading,
    } = useAmplitudeExperiment({
        experimentName: AmplitudeFeatureFlagNames.FTUE_WORKFLOW_ENABLED,
    });
    const {
        isEnrolled: isDisableOnboardingVideos,
        isLoading: isDisableOnboardingVideosLoading,
    } = useAmplitudeExperiment({
        experimentName: AmplitudeExperimentNames.DISABLE_ONBOARDING_VIDEOS,
        trackExposure: false,
    });
    const { pathname, push } = useRouter();
    const {
        activeCircle,
        isFetched: isFetchedActiveCircle,
        isError: isActiveCircleError,
    } = useActiveCircle();
    const {
        isConciergeOnboardingEnabled,
        isFetched: isConciergeOnboardingFetched,
    } = useIsConciergeOnboardingEnabled();
    const {
        isEnrolled: isConciergeGAEnrolled,
        isLoading: isConciergeGALoading,
    } = useAmplitudeExperiment({
        experimentName: AmplitudeFeatureFlagNames.CONCIERGE_GA_ENABLED,
    });
    const conciergeWorkflowId = isConciergeGAEnrolled
        ? WorkflowIds.CONCIERGE_GA_FTUE
        : WorkflowIds.CONCIERGE_FTUE;
    const [currentStep, setCurrentStep] = useState<string>();
    const [isRetakingSurvey, setIsRetakingSurvey] = useState<boolean>(false);
    const [workflowData, setWorkflowData] = useState<
        WorkflowStepDefinition['data'] | undefined
    >();

    const persistWorkflowStep = useCallback(
        (stepDefn: WorkflowStepDefinition | undefined) => {
            setCurrentStep(stepDefn?.defnId);
            setWorkflowData(stepDefn?.data);
            storeWorkflowStep({
                stepValue: stepDefn?.defnId ?? null,
                data: stepDefn?.data,
            });
        },
        []
    );

    // Initialize mutation hooks
    const { leaveWorkflowAsync } = useLeaveWorkflow();
    const {
        skipCachedVideoStepForwardAsync,
        isPending: isSkippingForwardPending,
    } = useSkipCachedVideoStepForward();
    const {
        skipCachedVideoStepBackwardAsync,
        isPending: isSkippingBackwardPending,
    } = useSkipCachedVideoStepBackward();
    const { startFtueWorkflowAsync, isPending: isStartingWorkflowPending } =
        useStartFtueWorkflow();
    const { updateFtueWorkflowStepAsync, isPending: isUpdatingStepPending } =
        useUpdateFtueWorkflowStep();
    const {
        restartFtueWorkflowAsync,
        isPending: isRestartFtueWorkflowPending,
    } = useRestartFtueWorkflow();
    const {
        getPreviousFtueWorkflowStepAsync,
        isPending: getPreviousFtueWorkflowStepPending,
    } = useGetPreviousFtueWorkflowStep();

    const {
        workflows = [],
        isFetched: isFetchedWorkflows,
        isPending: isPendingWorkflowInstances,
    } = useAllWorkflowInstances({
        workflowStates: [
            WorkflowWorkflowState.InProgress,
            WorkflowWorkflowState.Completed,
        ],
    });

    const unfinishedFtueWorkflows = useMemo(
        () =>
            workflows.filter(
                (workflow) =>
                    (workflow?.workflow.defnId === WorkflowIds.FTUE ||
                        workflow?.workflow.defnId ===
                            WorkflowIds.FTUE_NO_VIDEO) &&
                    workflow?.workflow.state ===
                        WorkflowWorkflowState.InProgress
            ),
        [workflows]
    );
    // Prefer the recipient's actual in-progress workflow instance over
    // the feature flag: a recipient who started onboarding before the
    // DISABLE_ONBOARDING_VIDEOS flag applied to them may have an existing
    // FTUE (with-video) instance even though the flag now says no-video.
    // Using the flag alone would target the wrong workflow instance on step
    // updates. Only fall back to the flag when there's no existing instance
    // (a brand-new start).
    const ftueWorkflowId = useMemo(
        () =>
            unfinishedFtueWorkflows[0]?.workflow.defnId ??
            (!isDisableOnboardingVideosLoading && isDisableOnboardingVideos
                ? WorkflowIds.FTUE_NO_VIDEO
                : WorkflowIds.FTUE),
        [
            unfinishedFtueWorkflows,
            isDisableOnboardingVideosLoading,
            isDisableOnboardingVideos,
        ]
    );
    const completedFtueWorkflows = useMemo(
        () =>
            workflows.filter(
                (workflow) =>
                    (workflow?.workflow.defnId === WorkflowIds.FTUE ||
                        workflow?.workflow.defnId ===
                            WorkflowIds.FTUE_NO_VIDEO) &&
                    workflow?.workflow.state === WorkflowWorkflowState.Completed
            ),
        [workflows]
    );

    const isPending = useMemo(
        () =>
            isSkippingForwardPending ||
            isSkippingBackwardPending ||
            isStartingWorkflowPending ||
            isUpdatingStepPending ||
            isRestartFtueWorkflowPending ||
            getPreviousFtueWorkflowStepPending ||
            isPendingWorkflowInstances,
        [
            isSkippingForwardPending,
            isSkippingBackwardPending,
            isStartingWorkflowPending,
            isUpdatingStepPending,
            isRestartFtueWorkflowPending,
            getPreviousFtueWorkflowStepPending,
            isPendingWorkflowInstances,
        ]
    );

    const isFetched = useMemo(
        () =>
            !isFtueWorkflowLoading &&
            isFetchedWorkflows &&
            isFetchedActiveCircle &&
            !isDisableOnboardingVideosLoading,
        [
            isFetchedActiveCircle,
            isFetchedWorkflows,
            isFtueWorkflowLoading,
            isDisableOnboardingVideosLoading,
        ]
    );

    const lastKnownWorkflowStep = useMemo((): LastKnownWorkflowStep => {
        const storedStep = getStoredWorkflowStep();
        return {
            stepValue: currentStep || storedStep?.stepValue || null,
            data: workflowData || storedStep?.data || undefined,
        };
    }, [currentStep, workflowData]);

    /*
     * The concierge handoff below owes the consumer a step from the render it
     * becomes due until the started workflow produces one. Held across the
     * whole handoff rather than on its mutations: the leave call's pending
     * state isn't wired up here, and the gap between it resolving and the
     * start registering is a render of its own - either way a consumer would
     * read it as "no step is coming" and end the workflow mid-handoff.
     */
    const isAwaitingConciergeHandoff =
        isConciergeOnboardingEnabled &&
        !hasSettledAbandon &&
        !!unfinishedFtueWorkflows.length;

    /* An in-progress instance with no known step is waiting on the restart
     * effect below once the permission has settled as "not concierge". */
    const isAwaitingRestart =
        !!isFtueWorkflowEnabled &&
        isConciergeOnboardingFetched &&
        !isConciergeOnboardingEnabled &&
        !hasSettledRestart &&
        !!unfinishedFtueWorkflows.length &&
        !lastKnownWorkflowStep.stepValue;

    /*
     * Both effects skip an unsettled permission, so which of the restart and
     * the concierge handoff will supply the step isn't known yet - but one of
     * them will once it settles. A consumer that ends the workflow here stores
     * a step that blocks the restart afterwards. A failed circle refresh isn't
     * retried until a focus or reconnect refetch, so it releases the hold
     * rather than leaving the consumer on a loading screen.
     */
    const isAwaitingPermissions =
        !isConciergeOnboardingFetched &&
        !isActiveCircleError &&
        !hasSettledRestart &&
        !hasSettledAbandon &&
        !!unfinishedFtueWorkflows.length &&
        !lastKnownWorkflowStep.stepValue;

    /*
     * A step is still on its way when a workflow mutation is in flight or one
     * of the effects below still owes the consumer one. Consumers render (and
     * run their own effects) before this provider's effects run, so without
     * this they can end the workflow while its first step is still being
     * fetched.
     *
     * Both handoffs settle through state rather than the `has...Workflow` refs
     * that guard their effects from firing twice: a ref read can't invalidate a
     * render, so a handoff that ends without a step - a failed restart, a
     * restart that resolves nothing - would leave a consumer waiting on a step
     * that is never coming.
     */
    const isResolvingStep =
        isPending ||
        isAwaitingConciergeHandoff ||
        isAwaitingRestart ||
        isAwaitingPermissions;

    const clearFtueVideoCache = useCallback(async () => {
        if (!isLoading && isAuthenticated) {
            // eslint-disable-next-line no-restricted-syntax
            localStorage.removeItem(
                (await getLocalStorageUserSha(
                    GOALS_TOPICS_INFO_VIDEO_ID,
                    user?.sub
                )) || GOALS_TOPICS_INFO_VIDEO_ID
            );
            // eslint-disable-next-line no-restricted-syntax
            localStorage.removeItem(
                (await getLocalStorageUserSha(
                    CONDITIONS_INFO_VIDEO_ID,
                    user?.sub
                )) || CONDITIONS_INFO_VIDEO_ID
            );
            // eslint-disable-next-line no-restricted-syntax
            localStorage.removeItem(
                (await getLocalStorageUserSha(
                    SCREENING_INFO_VIDEO_ID,
                    user?.sub
                )) || SCREENING_INFO_VIDEO_ID
            );
            // eslint-disable-next-line no-restricted-syntax
            localStorage.removeItem(
                (await getLocalStorageUserSha(
                    INTRO_TO_CARE_PLAN_VIDEO_ID,
                    user?.sub
                )) || INTRO_TO_CARE_PLAN_VIDEO_ID
            );
        }
    }, [user?.sub, isLoading, isAuthenticated]);

    /* Wrapper functions for mutation hooks */
    const skipCachedVideoStepForward = useCallback(
        async (stepDefnId: string, workflowDefnId: string) => {
            await skipCachedVideoStepForwardAsync({
                stepDefnId,
                workflowDefnId,
                onStepUpdate: persistWorkflowStep,
            });
        },
        [skipCachedVideoStepForwardAsync, persistWorkflowStep]
    );

    const skipCachedVideoStepBackward = useCallback(
        async (stepDefnId: string, workflowDefnId: string) => {
            await skipCachedVideoStepBackwardAsync({
                stepDefnId,
                workflowDefnId,
                onStepUpdate: persistWorkflowStep,
            });
        },
        [skipCachedVideoStepBackwardAsync, persistWorkflowStep]
    );

    /* If the user is coming from userblocker and already has a workflow in progress, restart the workflow to reset them to the beginning */
    const restartWorkflow = useCallback(
        async (workflowDefnId: string) => {
            return await restartFtueWorkflowAsync({
                workflowDefnId,
                onStepUpdate: persistWorkflowStep,
                onRetakingSurveyUpdate: setIsRetakingSurvey,
                clearFtueVideoCache,
            });
        },
        [restartFtueWorkflowAsync, clearFtueVideoCache, persistWorkflowStep]
    );

    const startWorkflow = useCallback(
        async ({
            workflowDefnId,
            shouldClearCurrentStep,
        }: {
            workflowDefnId: string;
            shouldClearCurrentStep?: boolean;
        }) => {
            if (shouldClearCurrentStep) {
                setCurrentStep(undefined);
                removeStoredWorkflowStep();
            }
            await startFtueWorkflowAsync({
                workflowDefnId,
                currentStep: shouldClearCurrentStep ? undefined : currentStep,
                userSub: user?.sub,
                onStepUpdate: persistWorkflowStep,
                skipCachedVideoStepForward,
            });
        },
        [
            startFtueWorkflowAsync,
            currentStep,
            user?.sub,
            persistWorkflowStep,
            skipCachedVideoStepForward,
        ]
    );

    const updateWorkflowStep = useCallback(
        async (
            workflowDefnId: string,
            stepDefnId: string,
            isSkipped?: boolean
        ) => {
            await updateFtueWorkflowStepAsync({
                workflowDefnId,
                stepDefnId,
                isSkipped,
                activeCircle,
                isFtueWorkflowLoading,
                isFtueWorkflowEnabled: Boolean(isFtueWorkflowEnabled),
                userSub: user?.sub,
                onStepUpdate: persistWorkflowStep,
                skipCachedVideoStepForward,
                restartWorkflow,
            });
        },
        [
            updateFtueWorkflowStepAsync,
            activeCircle,
            isFtueWorkflowLoading,
            isFtueWorkflowEnabled,
            user?.sub,
            persistWorkflowStep,
            skipCachedVideoStepForward,
            restartWorkflow,
        ]
    );

    function retakeMentalHealthSurvey() {
        setIsRetakingSurvey(true);
        storeIsRetakingSurvey(STORED_RETAKING_SURVEY_KEY);
    }

    async function getPreviousWorkflowStep(
        workflowDefnId: string,
        onPreviousStep?: (stepDefn: WorkflowStepDefinition | undefined) => void
    ) {
        await getPreviousFtueWorkflowStepAsync({
            workflowDefnId,
            userSub: user?.sub,
            // Invoke the caller's hook in the same call stack as the persist so
            // both state updates batch into a single render (no step flash).
            onStepUpdate: (stepDefn) => {
                persistWorkflowStep(stepDefn);
                // The previous step is already persisted; a throwing consumer
                // callback must not reject the whole navigation.
                try {
                    onPreviousStep?.(stepDefn);
                } catch (error) {
                    logger.error(
                        'Error in onPreviousStep callback after persisting previous workflow step',
                        {},
                        error as Error
                    );
                }
            },
            skipCachedVideoStepBackward,
        });
    }

    /* If the user's onboarding is now concierge-driven but they have an in-progress non-concierge FTUE workflow,
     * abandon the old workflow so useUserBlocker no longer redirects them to /onboarding.
     * This handles users who registered before the concierge beta and were later assigned to the experiment. */
    useEffect(() => {
        if (
            !isFetchedWorkflows ||
            !isConciergeOnboardingFetched ||
            !isConciergeOnboardingEnabled ||
            // An unsettled GA flag reads as not enrolled, which would start the legacy workflow for a GA user.
            isConciergeGALoading ||
            !unfinishedFtueWorkflows.length ||
            hasAbandonedConflictingWorkflow.current
        )
            return;

        const abandonConflictingWorkflow = async () => {
            // Claimed up front: starting the workflow changes currentStep, which
            // re-creates startWorkflow and re-runs this effect mid-flight.
            hasAbandonedConflictingWorkflow.current = true;
            try {
                await leaveWorkflowAsync({
                    workflowDefnId: unfinishedFtueWorkflows[0].workflow.defnId,
                });
                removeStoredStep(FtueStoredStepName);
                await startWorkflow({
                    workflowDefnId: conciergeWorkflowId,
                    shouldClearCurrentStep: true,
                });
            } catch (error) {
                logger.error(
                    'Error abandoning conflicting FTUE workflow for concierge user',
                    {
                        workflowDefnId:
                            unfinishedFtueWorkflows[0].workflow.defnId,
                    },
                    error as Error
                );
                setCurrentStep(FtueStepDefinitionIds.END_WORKFLOW);
                storeCurrentStep(
                    FtueStoredStepName,
                    FtueStepDefinitionIds.END_WORKFLOW
                );
                storeWorkflowStep({
                    stepValue: FtueStepDefinitionIds.END_WORKFLOW,
                    data: undefined,
                });
            } finally {
                setHasSettledAbandon(true);
            }
        };
        abandonConflictingWorkflow().catch((error) => {
            logger.error(
                'Error in FTUE workflow when trying to abandon old workflow and start a new one',
                {
                    unfinishedWorkflowDefnId:
                        unfinishedFtueWorkflows[0].workflow.defnId,
                    newWorkflowDefnId: conciergeWorkflowId,
                },
                error as Error
            );
        });
    }, [
        isFetchedWorkflows,
        isConciergeOnboardingFetched,
        isConciergeOnboardingEnabled,
        isConciergeGALoading,
        conciergeWorkflowId,
        unfinishedFtueWorkflows,
        leaveWorkflowAsync,
        startWorkflow,
    ]);

    useEffect(() => {
        if (
            !isFetchedWorkflows ||
            isPending ||
            !isFtueWorkflowEnabled ||
            hasRestartedWorkflow.current ||
            // An unsettled concierge permission reads as disabled, which would
            // restart an FTUE workflow for a concierge-driven user.
            !isConciergeOnboardingFetched ||
            isConciergeOnboardingEnabled || // Skip restart if the user's onboarding is now concierge-driven (e.g., after sponsorship change)
            // Dont go through restart flow if the user has no in progress workflows and already completed one of the same type before
            (completedFtueWorkflows.length > 0 &&
                !unfinishedFtueWorkflows.length)
        )
            return;

        const isRetakingSurveyStored = getIsRetakingSurvey(
            STORED_RETAKING_SURVEY_KEY
        );
        const storedStep = getStoredStep(FtueStoredStepName);

        /* True when the user is hard reloading the page when on the survey step after completing it once before */
        const isRefreshingSurveyRetry =
            !currentStep &&
            storedStep === FtueStepDefinitionIds.MENTAL_HEALTH_SCREENING &&
            isRetakingSurveyStored &&
            pathname === routes.onboarding({});

        if (
            !!unfinishedFtueWorkflows.length &&
            (!lastKnownWorkflowStep.stepValue || isRefreshingSurveyRetry)
        ) {
            const callRestart = async () => {
                hasRestartedWorkflow.current = true;
                try {
                    await restartWorkflow(ftueWorkflowId);
                    removeStoredStep(FtueStoredStepName);
                    removeStoredWorkflowStep();
                } catch (error) {
                    logger.error(
                        'Error restarting FTUE workflow in provider',
                        {
                            workflowsId: ftueWorkflowId,
                        },
                        error as Error
                    );
                    /*
                     * If restart call fails for any reason, set the step to END_WORKFLOW and redirect back to home screen to avoid blocking the user
                     * Since workflow instance itself is still in progress, the next time they start a session, it will try the restartWorkflow redirect again
                     */
                    setCurrentStep(FtueStepDefinitionIds.END_WORKFLOW);
                    storeWorkflowStep({
                        stepValue: FtueStepDefinitionIds.END_WORKFLOW,
                        data: undefined,
                    });
                    push(routes.dashboard).catch((err) =>
                        logger.error(
                            'Failed to navigate to dashboard',
                            {},
                            err as Error
                        )
                    );
                } finally {
                    setHasSettledRestart(true);
                }
            };
            callRestart().catch((err) =>
                logger.error(
                    'Failed to restart FTUE workflow',
                    {},
                    err as Error
                )
            );
        }
    }, [
        isFetchedWorkflows,
        restartWorkflow,
        currentStep,
        pathname,
        hasRestartedWorkflow,
        isFtueWorkflowEnabled,
        isConciergeOnboardingFetched,
        isConciergeOnboardingEnabled,
        lastKnownWorkflowStep.stepValue,
        isPending,
        unfinishedFtueWorkflows,
        completedFtueWorkflows,
        push,
        ftueWorkflowId,
    ]);

    return (
        <WorkflowContext.Provider
            value={{
                isFetched,
                isResolvingStep,
                isFtueWorkflowEnabled: !!(
                    !isFtueWorkflowLoading && isFtueWorkflowEnabled
                ),
                ftueWorkflowId,
                currentStep,
                setCurrentStep,
                lastKnownWorkflowStep,
                startWorkflow,
                restartWorkflow,
                updateWorkflowStep,
                getPreviousWorkflowStep,
                isRetakingSurvey,
                hasRestartedWorkflow: hasRestartedWorkflow.current,
                retakeMentalHealthSurvey,
                clearFtueVideoCache,
            }}
        >
            {children}
        </WorkflowContext.Provider>
    );
}
